/**
 * Definição central dos planos, preços e limites do FIVI360 (Beta).
 * Fonte única de verdade para enforcement, checkout UX e copy comercial.
 *
 * Formato persistido em `users.plan`:
 *   - Bootstrap Starter: `"starter"`
 *   - Assinatura Stripe / sistema / manual: `{ id, status, source, ... }`
 *
 * IDs reconhecidos: starter | professional | studio | enterprise.
 * Valores desconhecidos → Starter (sem entitlement pago).
 *
 * `source` NÃO altera entitlement (ver `normalizeUserPlan`).
 * Valores canônicos: stripe | system | manual.
 *
 * Estimativa de imagens: ~5 MB por panorama. Quantidade aproximada;
 * consumo real varia com resolução, formato e compressão.
 */

export const PLAN_IDS = {
  STARTER: "starter",
  PROFESSIONAL: "professional",
  STUDIO: "studio",
  ENTERPRISE: "enterprise",
};

/**
 * Origem do plano em `users.plan.source` (metadado; não é entitlement).
 * - stripe: Checkout / assinatura Stripe
 * - system: downgrade automático (ex.: subscription.deleted)
 * - manual: concessão Admin (sem assinatura Stripe)
 */
export const PLAN_SOURCES = {
  STRIPE: "stripe",
  SYSTEM: "system",
  MANUAL: "manual",
};

/** @typedef {'starter' | 'professional' | 'studio' | 'enterprise'} PlanId */

/** @typedef {'stripe' | 'system' | 'manual' | string} PlanSource */

/**
 * @typedef {Object} UserPlan
 * @property {string} [id]
 * @property {string} [status]
 * @property {string} [source]
 * @property {boolean} [cancelAtPeriodEnd]
 * @property {Date | null} [updatedAt]
 */

/** @typedef {string | UserPlan | null | undefined} UserPlanRaw */

/**
 * @typedef {Object} PlanLimits
 * @property {string} name
 * @property {string} displayName
 * @property {string} tagline
 * @property {string} description
 * @property {string | null} [badge]
 * @property {number | null} monthlyPrice — BRL; 0 = grátis; null = sob consulta
 * @property {number | null} approximateImageCount — estimativa (~5 MB/imagem); null = personalizado
 * @property {boolean} checkoutEnabled
 * @property {boolean} [contactOnly]
 * @property {number | null} maxProjects — null = ilimitado
 * @property {number | null} maxTotalImages — null = ilimitado (enforcement; distinto da estimativa)
 * @property {number} maxStorageBytes
 * @property {boolean} hotspotsEnabled
 * @property {boolean} publicPortfolioEnabled
 * @property {boolean} publicVisibilityEnabled
 * @property {boolean} projectEmbedEnabled
 * @property {boolean} analyticsEnabled
 * @property {boolean} advancedAnalytics
 * @property {boolean} prioritySupport
 * @property {boolean} multiuserEnabled
 * @property {boolean} customDomainEnabled
 * @property {boolean} whiteLabelEnabled
 * @property {string | null} [status]
 * @property {string} priceLabel
 * @property {string} [periodLabel]
 * @property {string[]} featureBullets
 */

const MB = 1024 * 1024;
const GB = 1024 * 1024 * 1024;

/** Bytes de referência por imagem panorâmica para estimativas comerciais. */
export const APPROX_IMAGE_BYTES = 5 * MB;

/**
 * Texto padrão sobre estimativa de quantidade de imagens.
 * Exibir junto a tabelas/cards de planos.
 */
export const STORAGE_IMAGE_ESTIMATE_NOTE =
  "A quantidade de panoramas é uma estimativa e pode variar conforme o tamanho dos arquivos.";

/**
 * Rótulo comercial do preço mensal (BRL).
 * Planos pagos usam sempre duas casas: R$ 49,90 / R$ 199,90.
 *
 * @param {number | null} monthlyPrice
 * @returns {string}
 */
export function formatMonthlyPriceLabel(monthlyPrice) {
  if (monthlyPrice === null || monthlyPrice === undefined) {
    return "Sob consulta";
  }

  if (monthlyPrice === 0) {
    return "Grátis";
  }

  const formatted = Number(monthlyPrice).toFixed(2).replace(".", ",");
  return `R$ ${formatted}`;
}

/**
 * @param {number | null} approximateImageCount
 * @returns {string | null}
 */
export function formatApproximateImageCountLabel(approximateImageCount) {
  if (approximateImageCount == null) {
    return "Quantidade de imagens personalizada";
  }

  return `Aproximadamente ${approximateImageCount} imagens panorâmicas`;
}

/**
 * @param {number} bytes
 * @param {{ from?: boolean }} [options]
 * @returns {string}
 */
export function formatStorageLimitLabel(bytes, options = {}) {
  const prefix = options.from ? "A partir de " : "";

  if (bytes >= GB) {
    const gb = bytes / GB;
    const label = Number.isInteger(gb) ? `${gb}` : gb.toFixed(2);
    return `${prefix}${label} GB de armazenamento`;
  }

  return `${prefix}${Math.round(bytes / MB)} MB de armazenamento`;
}

/** @type {Record<PlanId, PlanLimits>} */
export const PLAN_LIMITS = {
  [PLAN_IDS.STARTER]: {
    name: PLAN_IDS.STARTER,
    displayName: "Starter",
    tagline: "Para conhecer o FIVI360 e criar seus primeiros projetos",
    description:
      "Ideal para conhecer a plataforma e criar suas primeiras apresentações em 360°.",
    badge: null,
    monthlyPrice: 0,
    approximateImageCount: 5,
    checkoutEnabled: false,
    contactOnly: false,
    maxProjects: 2,
    maxTotalImages: 10,
    maxStorageBytes: 25 * MB,
    hotspotsEnabled: false,
    publicPortfolioEnabled: false,
    publicVisibilityEnabled: false,
    projectEmbedEnabled: false,
    analyticsEnabled: false,
    advancedAnalytics: false,
    prioritySupport: false,
    multiuserEnabled: false,
    customDomainEnabled: false,
    whiteLabelEnabled: false,
    status: null,
    priceLabel: "Grátis",
    periodLabel: "",
    featureBullets: [
      "Até 2 projetos",
      "25 MB de armazenamento",
      "Aproximadamente 5 imagens panorâmicas",
      "Compartilhamento por link",
    ],
  },
  [PLAN_IDS.PROFESSIONAL]: {
    name: PLAN_IDS.PROFESSIONAL,
    displayName: "Professional",
    tagline: "Para arquitetos e designers que apresentam projetos a clientes",
    description:
      "Transforme suas apresentações em uma experiência profissional e compartilhe seus projetos com clientes de forma imersiva.",
    badge: "Mais escolhido",
    monthlyPrice: 49.90,
    approximateImageCount: 50,
    checkoutEnabled: true,
    contactOnly: false,
    maxProjects: null,
    maxTotalImages: null,
    maxStorageBytes: 250 * MB,
    hotspotsEnabled: true,
    publicPortfolioEnabled: true,
    publicVisibilityEnabled: true,
    projectEmbedEnabled: true,
    analyticsEnabled: true,
    advancedAnalytics: false,
    prioritySupport: false,
    multiuserEnabled: false,
    customDomainEnabled: false,
    whiteLabelEnabled: false,
    status: null,
    priceLabel: "R$ 49,90",
    periodLabel: "/mês",
    featureBullets: [
      "Projetos ilimitados",
      "250 MB de armazenamento",
      "Aproximadamente 50 imagens panorâmicas",
      "Portfólio público",
      "Experiências 360° interativas",
      "Hotspots informativos",
      "Navegação entre ambientes",
      "Incorporação no seu site",
      // "Analytics",
    ],
  },
  [PLAN_IDS.STUDIO]: {
    name: PLAN_IDS.STUDIO,
    displayName: "Studio",
    tagline: "Para escritórios com mais projetos e maior volume de apresentações",
    description:
      "Mais espaço, mais capacidade e recursos preparados para equipes que gerenciam múltiplos projetos simultaneamente.",
    badge: "",
    monthlyPrice: 199.90,
    approximateImageCount: 400,
    checkoutEnabled: true,
    contactOnly: false,
    maxProjects: null,
    maxTotalImages: null,
    maxStorageBytes: 2 * GB,
    hotspotsEnabled: true,
    publicPortfolioEnabled: true,
    publicVisibilityEnabled: true,
    projectEmbedEnabled: true,
    analyticsEnabled: true,
    advancedAnalytics: true,
    prioritySupport: true,
    multiuserEnabled: false,
    customDomainEnabled: false,
    whiteLabelEnabled: false,
    status: null,
    priceLabel: "R$ 199,90",
    periodLabel: "/mês",
    featureBullets: [
      "Tudo do Professional",
      "2 GB de armazenamento",
      "Aproximadamente 400 imagens panorâmicas",
      "Analytics avançado",
      "Suporte prioritário",
    ],
  },
  [PLAN_IDS.ENTERPRISE]: {
    name: PLAN_IDS.ENTERPRISE,
    displayName: "Enterprise",
    tagline: "Para empresas com necessidades específicas",
    description:
      "Uma solução corporativa para equipes, empreendimentos e operações em escala.",
    badge: null,
    monthlyPrice: null,
    approximateImageCount: null,
    checkoutEnabled: false,
    contactOnly: true,
    maxProjects: null,
    maxTotalImages: null,
    maxStorageBytes: 10 * GB,
    hotspotsEnabled: true,
    publicPortfolioEnabled: true,
    publicVisibilityEnabled: true,
    projectEmbedEnabled: true,
    analyticsEnabled: true,
    advancedAnalytics: true,
    prioritySupport: true,
    multiuserEnabled: true,
    customDomainEnabled: true,
    whiteLabelEnabled: true,
    status: null,
    priceLabel: "Sob consulta",
    periodLabel: "",
    featureBullets: [
      "Tudo do Studio",
      "Armazenamento personalizado",
      "Volume de imagens personalizado",
      "Multiplos usuários",
      "Workspaces compartilhados",
      "Permissões de equipe",
      "Domínio personalizado",
      "Atendimento dedicado"
    ],
  },
};

/** Alias explícito da fonte comercial central. */
export const PLAN_CONFIG = PLAN_LIMITS;

/** Ordem de exibição na página de planos e landing */
export const PLAN_ORDER = [
  PLAN_IDS.STARTER,
  PLAN_IDS.PROFESSIONAL,
  PLAN_IDS.STUDIO,
  PLAN_IDS.ENTERPRISE,
];

/** Hierarquia de planos (maior = mais recursos). */
export const PLAN_TIER = {
  [PLAN_IDS.STARTER]: 0,
  [PLAN_IDS.PROFESSIONAL]: 1,
  [PLAN_IDS.STUDIO]: 2,
  [PLAN_IDS.ENTERPRISE]: 3,
};

const RECOGNIZED_PLAN_IDS = {
  starter: PLAN_IDS.STARTER,
  professional: PLAN_IDS.PROFESSIONAL,
  studio: PLAN_IDS.STUDIO,
  enterprise: PLAN_IDS.ENTERPRISE,
};

/** Statuses que mantêm os limites do plano pago (objeto `users.plan`). */
export const ACTIVE_PLAN_STATUSES = new Set(["active", "trialing"]);

/**
 * Extrai `users.plan.source` (metadado). String root / ausente → "".
 *
 * @param {UserPlanRaw} raw
 * @returns {string}
 */
export function getPlanSource(raw) {
  if (raw && typeof raw === "object" && typeof raw.source === "string") {
    return raw.source.toLowerCase().trim();
  }

  return "";
}

/**
 * @param {string | null | undefined} source
 * @returns {boolean}
 */
export function isManualPlanSource(source) {
  return (source ?? "").toLowerCase().trim() === PLAN_SOURCES.MANUAL;
}

/**
 * Normaliza um ID de plano bruto (string).
 * Apenas IDs canônicos; aliases históricos (`free`, `pro`) não são reconhecidos.
 *
 * @param {string | undefined | null} planId
 * @returns {PlanId}
 */
export function normalizePlanIdFromString(planId) {
  const key = (planId ?? "").toLowerCase().trim();
  return RECOGNIZED_PLAN_IDS[key] ?? PLAN_IDS.STARTER;
}

/**
 * Resolve o plano efetivo do usuário a partir de `users.plan`.
 * Aceita string canônica (`"starter"`) ou objeto `{ id, status, source }`.
 *
 * Regras:
 * - Objeto com `status` diferente de `active` / `trialing` → Starter
 * - Objeto com `id` válido e status ativo (ou ausente) → limites do `id`
 * - String canônica → limites do id
 * - Valor desconhecido → Starter
 *
 * @param {UserPlanRaw} raw
 * @returns {PlanId}
 */
export function normalizeUserPlan(raw) {
  if (typeof raw === "string") {
    return normalizePlanIdFromString(raw);
  }

  if (raw && typeof raw === "object") {
    const status =
      typeof raw.status === "string" ? raw.status.toLowerCase().trim() : "";

    if (status && !ACTIVE_PLAN_STATUSES.has(status)) {
      return PLAN_IDS.STARTER;
    }

    return normalizePlanIdFromString(raw.id);
  }

  return PLAN_IDS.STARTER;
}

/**
 * @param {UserPlanRaw} raw — `users.plan`
 * @returns {PlanId}
 */
export function normalizePlanId(raw) {
  return normalizeUserPlan(raw);
}

/**
 * @param {UserPlanRaw} raw
 * @returns {PlanLimits}
 */
export function getPlanLimits(raw) {
  const normalized = normalizeUserPlan(raw);
  return PLAN_LIMITS[normalized];
}

/**
 * @param {PlanId} planId
 * @returns {number}
 */
export function getPlanTier(planId) {
  return PLAN_TIER[planId] ?? 0;
}

/**
 * @param {PlanId} currentPlanId
 * @param {PlanId} targetPlanId
 * @returns {boolean}
 */
export function isPlanAtOrAbove(currentPlanId, targetPlanId) {
  return getPlanTier(currentPlanId) >= getPlanTier(targetPlanId);
}

/**
 * Incorporação em websites (Embed) — Professional e planos superiores.
 * Aceita `users.plan` bruto (string ou objeto com status).
 *
 * @param {UserPlanRaw} plan
 * @returns {boolean}
 */
export function canUseProjectEmbed(plan) {
  return getPlanLimits(plan).projectEmbedEnabled === true;
}

/**
 * Representação oficial de recurso ilimitado: `null` (ou `undefined` em dados parciais).
 * Não usar `-1`, `Infinity` nem string `"unlimited"` em componentes.
 *
 * @param {number | null | undefined} value
 * @returns {boolean}
 */
export function isUnlimited(value) {
  return value === null || value === undefined;
}

/**
 * Alias explícito para consumo/UI — mesma regra que `isUnlimited`.
 *
 * @param {number | null | undefined} limit
 * @returns {boolean}
 */
export function isUnlimitedLimit(limit) {
  return isUnlimited(limit);
}

/**
 * @param {number} bytes
 * @returns {string}
 */
export function formatStorageBytes(bytes) {
  if (bytes < 1024) {
    return `${bytes} B`;
  }

  if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(1)} KB`;
  }

  if (bytes < 1024 * 1024 * 1024) {
    return `${Math.round(bytes / (1024 * 1024))} MB`;
  }

  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

/**
 * Percentual de uso para barras/alerta. Recursos ilimitados nunca geram % de alerta.
 *
 * @param {number} current
 * @param {number | null | undefined} limit
 * @returns {number} 0–100
 */
export function usagePercentage(current, limit) {
  if (isUnlimitedLimit(limit)) {
    return 0;
  }

  if (limit <= 0) {
    return current > 0 ? 100 : 0;
  }

  return Math.min(100, Math.round((current / limit) * 100));
}
